import { QueryClient } from "@tanstack/react-query";
import { center } from "@turf/turf";
import { queryKeys } from "common/queryKeys";
import { featureHasValidGeometry } from "common/utils/mapGeometry";
import { coverInKgPerHaToDryMatter } from "common/utils/helpers";
import type { Feature, FeatureCollection, Point } from "geojson";
import { sum } from "lodash";
// Use direct imports to avoid circular dependency with model/index.ts
import { AnimalGroup, StockClass } from "model/animalGroupTypes";
import { ArableField } from "model/arableFieldTypes";
import { Farm } from "model/farmTypes";
import { Field } from "model/fieldTypes";

/** When farm context is set, only entities owned by that farm pass through. */
export function belongsToFarm<T extends { farm: string }>(
  entity: T | undefined,
  farmGuid: string | null | undefined
): entity is T {
  if (!entity) return false;
  if (!farmGuid) return true;
  return entity.farm === farmGuid;
}

export function filterEntitiesForFarm<T extends { farm: string }>(
  entities: T[],
  farmGuid: string | null | undefined
): T[] {
  if (!farmGuid) return entities;
  return entities.filter((entity) => entity.farm === farmGuid);
}

/**
 * Helper function to compute field geoms from fields array
 */
export function getAllFieldGeomsFromFields(
  fields: (Field | ArableField)[]
): FeatureCollection {
  const features: Feature[] = fields.flatMap((f) => {
    if (!featureHasValidGeometry(f.geom)) {
      return [];
    }
    const geom = f.geom;
    return [
      {
        type: geom.type,
        geometry: geom.geometry,
        properties: {
          ...geom.properties,
          id: f.guid,
          dryMatterNow: "dryMatterNow" in f ? f.dryMatterNow : undefined,
        },
      },
    ];
  });
  return {
    type: "FeatureCollection",
    features,
  };
}

/**
 * Helper function to compute arable field geoms from arable fields array
 */
export function getAllArableFieldGeomsFromFields(
  arableFields: ArableField[]
): FeatureCollection {
  const features: Feature[] = arableFields.flatMap((f) => {
    if (!featureHasValidGeometry(f.geom)) {
      return [];
    }
    const geom = f.geom;
    return [
      {
        type: geom.type,
        geometry: geom.geometry,
        properties: {
          ...geom.properties,
          id: f.guid,
        },
      },
    ];
  });
  return {
    type: "FeatureCollection",
    features,
  };
}

/**
 * Helper function to map field centers by ID from array
 */
export function mapFieldsCentersByIdFromArray(
  fields: Field[]
): Record<string, Feature<Point>> {
  const centerByGuid: Record<string, Feature<Point>> = {};
  fields.forEach((field) => {
    if (!featureHasValidGeometry(field.geom)) {
      return;
    }
    centerByGuid[field.guid] = center(field.geom);
  });
  return centerByGuid;
}

/**
 * Helper function to map arable field centers by ID from array
 */
export function mapArableFieldsCentersByIdFromArray(
  arableFields: ArableField[]
): Record<string, Feature<Point>> {
  const centerByGuid: Record<string, Feature<Point>> = {};
  arableFields.forEach((arableField) => {
    if (!featureHasValidGeometry(arableField.geom)) {
      return;
    }
    centerByGuid[arableField.guid] = center(arableField.geom);
  });
  return centerByGuid;
}

/**
 * Helper function to get fields with no tiles from array
 */
export function getFieldsWithNoTilesFromArray(fields: Field[]): string[] {
  return fields.reduce((acc: Array<string>, field) => {
    if (field.tiles?.length === 0) {
      acc.push(field.guid);
    }
    return acc;
  }, []);
}

/**
 * Helper function to get arable fields with no tiles from array
 */
export function getArableFieldsWithNoTilesFromArray(
  arableFields: ArableField[]
): string[] {
  return arableFields.reduce((acc: Array<string>, arableField) => {
    if (arableField.tiles?.length === 0) {
      acc.push(arableField.guid);
    }
    return acc;
  }, []);
}

/**
 * Helper function to filter fields with not deleted boundaries
 */
export function filterFieldsWithNotDeletedBoundaries(
  fields: Field[],
  tmpDeletedFeatures: string[]
): Field[] {
  return fields.filter(
    (field) => tmpDeletedFeatures.indexOf(field.guid) === -1
  );
}

/**
 * Helper function to filter arable fields with not deleted boundaries
 */
export function filterArableFieldsWithNotDeletedBoundaries(
  arableFields: ArableField[],
  tmpDeletedFeatures: string[]
): ArableField[] {
  return arableFields.filter(
    (arableField) => tmpDeletedFeatures.indexOf(arableField.guid) === -1
  );
}

/**
 * Helper function to compute total area from fields array
 */
export function getTotalAreaFromArray(fields: Field[]): number {
  return fields.reduce((acc, field) => {
    if (!field.invalidatedTimestamp) {
      acc += field.area / 10000;
    }
    return acc;
  }, 0);
}

/**
 * Helper function to compute total arable area from arable fields array
 */
export function getTotalArableAreaFromArray(
  arableFields: ArableField[]
): number {
  return arableFields.reduce((acc, arableField) => {
    if (!arableField.invalidatedTimestamp) {
      acc += arableField.area / 10000;
    }
    return acc;
  }, 0);
}

/**
 * Helper function to compute total farm area (fields + arable fields)
 */
export function getTotalFarmAreaFromArrays(
  fields: Field[],
  arableFields: ArableField[]
): number {
  return (
    getTotalAreaFromArray(fields) + getTotalArableAreaFromArray(arableFields)
  );
}

/**
 * Helper function to compute total average dry matter from array
 */
export function getTotalAvgDMFromArray(fields: Field[]): number {
  const totalArea = getTotalAreaFromArray(fields);
  if (totalArea === 0) return 0;

  const sumOfDM = fields.reduce((acc, field) => {
    if (!field.invalidatedTimestamp) {
      acc += (field.dryMatterNow * field.area) / 10000;
    }
    return acc;
  }, 0);
  return Math.round(sumOfDM / totalArea);
}

/**
 * Helper function to get field count from array
 * Helper function
 * @param fields - Array of fields
 * @returns The count of fields
 */
export function getFieldCountFromArray(fields: Field[]): number {
  return fields.length;
}

/**
 * Helper function to compute total field count (fields + arable fields)
 */
export function getTotalFieldCountFromArrays(
  fields: Field[],
  arableFields: ArableField[]
): number {
  return fields.length + arableFields.length;
}

/**
 * Helper function to get all fields for an animal group ID from array
 */
export function getAllFieldsForAnimalGroupIdFromArray(
  fields: Field[],
  animalGroupId?: string
): Field[] {
  if (!animalGroupId) return [];
  return fields.filter((field) => field.animalGroup === animalGroupId);
}

/**
 * Helper function to get field with animals for an animal group ID from array
 * Returns the field that currently has animals (animalGroupNow matches animalGroup)
 */
export function getFieldWithAnimalsForAnimalGroupIdFromArray(
  fields: Field[],
  animalGroupId?: string
): Field | undefined {
  if (!animalGroupId) return undefined;
  return getAllFieldsForAnimalGroupIdFromArray(fields, animalGroupId)
    .filter((field) => !!field.animalGroupNow)
    .pop();
}

/**
 * Helper function to get field that currently has animals for a given animal group
 * Helper function
 * Finds the field where animalGroupNow matches the given animal group ID
 * @param fields - Array of all fields
 * @param animalGroupId - The animal group ID to find
 * @returns The field with animals, or undefined if not found
 */
export function getCurrentFieldWithAnimalsFromArray(
  fields: Field[],
  animalGroupId?: string
): Field | undefined {
  if (!animalGroupId) return undefined;
  return fields.find((field) => field.animalGroupNow === animalGroupId);
}

/**
 * Helper function to get field geoms for an animal group from array
 */
export function getFieldGeomsForAnimalGroupFromArray(
  fields: Field[],
  animalGroupId: string
): FeatureCollection {
  const animalGroupFields = getAllFieldsForAnimalGroupIdFromArray(
    fields,
    animalGroupId
  );
  const features = animalGroupFields
    .map((f) => f.geom)
    .filter(featureHasValidGeometry);
  return {
    type: "FeatureCollection",
    features,
  };
}

/**
 * Helper function to calculate available dry matter for an animal group from array
 * Helper function
 * @param fields - Array of fields for the animal group
 * @param animalGroup - The animal group (optional, for postGrazingTarget)
 * @returns The total available dry matter in kg
 */
export function getAvailableDryMatterForAnimalGroupIdFromArray(
  fields: Field[],
  animalGroup?: AnimalGroup | null
): number {
  return sum(
    fields.map((field) =>
      coverInKgPerHaToDryMatter(
        field.dryMatterNow - (animalGroup?.postGrazingTarget || 0),
        field.area
      )
    )
  );
}

/**
 * Helper function to get fields with animals and their centers/colors from array
 * Helper function
 * Filters fields that have both animalGroup and animalGroupNow, and adds center and color properties
 * @param fields - Array of all fields
 * @param colorByAnimalGroupId - Mapping of animal group GUID to color (from getAnimalGroupColorMapping)
 * @param fieldCentersById - Mapping of field GUID to center point (from mapFieldsCentersByIdFromArray)
 * @returns Array of fields with animals, enriched with center and color properties
 */
export function getFieldsWithAnimalsCentersFromArray(
  fields: Field[],
  colorByAnimalGroupId: Record<string, string>,
  fieldCentersById: Record<string, Feature<Point>>
): Array<
  Field & { center: Feature<Point> | undefined; color: string | undefined }
> {
  return fields
    .filter((field) => field.animalGroup && field.animalGroupNow)
    .map((field) => ({
      ...field,
      center: fieldCentersById[field.guid],
      color: colorByAnimalGroupId[field.animalGroup as string],
    }));
}

/**
 * Helper function to get the latest tile ID for each field from array
 * Helper function
 * Extracts the last tile ID from each field's tiles array
 * @param fields - Array of fields
 * @returns Array of tile IDs (strings)
 */
export function getLatestFieldTilesFromArray(fields: Field[]): string[] {
  const tileIds = fields.map((f) => {
    return f.tiles?.slice(-1)[0];
  });
  return tileIds.filter((t): t is string => !!t);
}

/**
 * Helper function to get the latest tile ID for each arable field from array
 * Helper function
 * Extracts the last tile ID from each arable field's tiles array
 * @param arableFields - Array of arable fields
 * @returns Array of tile IDs (strings)
 */
export function getLatestArableFieldTilesFromArray(
  arableFields: ArableField[]
): string[] {
  const tileIds = arableFields.map((f) => {
    return f.tiles?.slice(-1)[0];
  });
  return tileIds.filter((t): t is string => !!t);
}

/**
 * Helper function to get all field IDs (guids) from an array of fields
 * Helper function
 * Extracts the guid from each field
 * @param fields - Array of fields
 * @returns Array of field IDs (guids as strings)
 */
export function getFieldIdsFromArray(fields: Field[]): string[] {
  return fields.map((field) => field.guid);
}

/**
 * Helper function to get silage fields (fields without an animal group) from array
 * Helper function
 * Filters fields that don't have an animalGroup assigned
 * @param fields - Array of all fields
 * @returns Array of silage fields (fields without animalGroup)
 */
export function getSilageFieldsFromArray(fields: Field[]): Field[] {
  return fields.filter((field) => !field.animalGroup);
}

/**
 * Helper function to calculate average cover for silage fields from array
 * Helper function
 * Calculates the weighted average cover based on dry matter and area
 * @param fields - Array of all fields (will be filtered to silage fields internally)
 * @returns The average cover in kg/ha, or 0 if there are no silage fields
 */
export function getAverageCoverForSilageFromArray(fields: Field[]): number {
  const silageFields = getSilageFieldsFromArray(fields);

  if (silageFields.length === 0) {
    return 0;
  }

  const totalDryMatter = sum(
    silageFields.map((field) =>
      coverInKgPerHaToDryMatter(field.dryMatterNow, field.area)
    )
  );

  const totalArea = sum(silageFields.map((field) => field.area / 10000));

  if (totalArea === 0) {
    return 0;
  }

  return totalDryMatter / totalArea;
}

/**
 * Grass/pasture fields for one farm from the React Query cache.
 * Uses the farm's field ID list — never scans the whole fields domain.
 */
export function getAllFieldsFromCache(
  queryClient: QueryClient,
  farmGuid: string
): Field[] {
  const farm = queryClient.getQueryData<Farm>(queryKeys.farms.detail(farmGuid));
  const fieldIds = farm?.fields ?? [];

  return fieldIds
    .map((fieldId) =>
      queryClient.getQueryData<Field>(queryKeys.fields.detail(fieldId))
    )
    .filter((field): field is Field => belongsToFarm(field, farmGuid));
}

/**
 * Arable fields for one farm from the React Query cache.
 * Uses the farm-scoped arable ID list — never scans the whole arableFields domain.
 */
export function getAllArableFieldsFromCache(
  queryClient: QueryClient,
  farmGuid: string
): ArableField[] {
  const fieldIds =
    queryClient.getQueryData<string[]>(queryKeys.arableFields.ids(farmGuid)) ??
    [];

  return fieldIds
    .map((fieldId) =>
      queryClient.getQueryData<ArableField>(
        queryKeys.arableFields.detail(fieldId)
      )
    )
    .filter((field): field is ArableField => belongsToFarm(field, farmGuid));
}

/**
 * Helper function to get fields with no tiles for not-deleted boundaries from array
 * Combines getFieldsWithNoTilesFromArray and filterFieldsWithNotDeletedBoundaries
 * @param fields - Array of all fields
 * @param tmpDeletedFeatures - Array of temporarily deleted feature IDs
 * @returns Array of field IDs that have no tiles and are not deleted
 */
export function getFieldsWithNoTilesForNotDeletedBoundariesFromArray(
  fields: Field[],
  tmpDeletedFeatures: string[]
): string[] {
  const fieldsWithNoTiles = getFieldsWithNoTilesFromArray(fields);
  return fieldsWithNoTiles.filter(
    (id) => tmpDeletedFeatures.indexOf(id) === -1
  );
}

/**
 * Helper function to get arable fields with no tiles for not-deleted boundaries from array
 * Combines getArableFieldsWithNoTilesFromArray and filterArableFieldsWithNotDeletedBoundaries
 * @param arableFields - Array of all arable fields
 * @param tmpDeletedFeatures - Array of temporarily deleted feature IDs
 * @returns Array of arable field IDs that have no tiles and are not deleted
 */
export function getArableFieldsWithNoTilesForNotDeletedBoundariesFromArray(
  arableFields: ArableField[],
  tmpDeletedFeatures: string[]
): string[] {
  const arableFieldsWithNoTiles =
    getArableFieldsWithNoTilesFromArray(arableFields);
  return arableFieldsWithNoTiles.filter(
    (id) => tmpDeletedFeatures.indexOf(id) === -1
  );
}

/**
 * Helper function to get field centers with no tiles for not-deleted boundaries from array
 * Helper function
 * @param fields - Array of all fields
 * @param tmpDeletedFeatures - Array of temporarily deleted feature IDs
 * @param fieldCentersById - Mapping of field GUID to center point
 * @returns Array of objects with guid and center properties
 */
export function getFieldsCenterWithNoTilesForNotDeletedBoundariesFromArray(
  fields: Field[],
  tmpDeletedFeatures: string[],
  fieldCentersById: Record<string, Feature<Point>>
): Array<{ guid: string; center: Feature<Point> }> {
  return fields
    .filter(
      (field) =>
        field.tiles?.length === 0 &&
        tmpDeletedFeatures.indexOf(field.guid) === -1
    )
    .map((field) => ({
      guid: field.guid,
      center: fieldCentersById[field.guid],
    }));
}

/**
 * Helper function to get arable field centers with no tiles for not-deleted boundaries from array
 * Helper function
 * @param arableFields - Array of all arable fields
 * @param tmpDeletedFeatures - Array of temporarily deleted feature IDs
 * @param arableFieldCentersById - Mapping of arable field GUID to center point
 * @returns Array of objects with guid and center properties
 */
export function getArableFieldsCenterWithNoTilesForNotDeletedBoundariesFromArray(
  arableFields: ArableField[],
  tmpDeletedFeatures: string[],
  arableFieldCentersById: Record<string, Feature<Point>>
): Array<{ guid: string; center: Feature<Point> }> {
  return arableFields
    .filter(
      (arableField) =>
        arableField.tiles?.length === 0 &&
        tmpDeletedFeatures.indexOf(arableField.guid) === -1
    )
    .map((arableField) => ({
      guid: arableField.guid,
      center: arableFieldCentersById[arableField.guid],
    }));
}

/**
 * Helper function to calculate total farm animal count from stock classes array
 * Helper function
 * @param stockClasses - Array of stock classes
 * @returns Total count of all animals across all stock classes
 */
export function getTotalFarmAnimalCountFromArray(
  stockClasses: StockClass[]
): number {
  return stockClasses.reduce((total, stockClass) => {
    return total + (stockClass.animalCount || 0);
  }, 0);
}

/**
 * Helper function to calculate total available dry matter for pasture fields from array
 * Helper function
 * @param fields - Array of fields (will be filtered to pasture fields internally)
 * @param animalGroups - Array of animal groups (for post-grazing target)
 * @returns Total available dry matter in kg
 */
export function getTotalAvailableDryMatterForPasturesFromArrays(
  fields: Field[],
  animalGroups: AnimalGroup[]
): number {
  // Filter to pasture fields (fields with animal groups)
  const pastureFields = fields.filter((field) => field.animalGroup);

  // Create a map of animal group GUIDs to their postGrazingTarget
  const postGrazingTargetByAnimalGroup: Record<string, number> = {};
  animalGroups.forEach((ag) => {
    postGrazingTargetByAnimalGroup[ag.guid] = ag.postGrazingTarget || 0;
  });

  // Calculate available dry matter for each pasture field
  return sum(
    pastureFields.map((field) => {
      const postGrazingTarget = field.animalGroup
        ? postGrazingTargetByAnimalGroup[field.animalGroup] || 0
        : 0;
      const availableDM = field.dryMatterNow - postGrazingTarget;
      return coverInKgPerHaToDryMatter(Math.max(0, availableDM), field.area);
    })
  );
}
